import { useEffect, useState } from "react";
import ShopStorefrontPreview from "../components/ShopStorefrontPreview";

function ShopSettingsPage({ error, isSaving, listings, mediaUrl, onBack, onDeleteShop, onPreviewShop, onSaveBranding, shop }) {
  const [shopName, setShopName] = useState("");
  const [shopColor, setShopColor] = useState("#1f6f5f");
  const [logo, setLogo] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");

  useEffect(() => {
    setShopName(shop?.shop_name || "");
    setShopColor(shop?.shop_color || "#1f6f5f");
    setDescription(shop?.description || "");
    setCategories(shop?.shop_categories || []);
  }, [shop]);

  useEffect(() => {
    if (!logo) {
      setLogoPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(logo);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logo]);

  useEffect(() => {
    if (!cover) {
      setCoverPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(cover);
    setCoverPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [cover]);

  const previewShop = {
    ...shop,
    shop_name: shopName,
    shop_color: shopColor,
    shop_logo_url: logoPreviewUrl || shop?.shop_logo_url,
    cover_url: coverPreviewUrl || shop?.cover_url,
    description,
  };

  return (
    <section className="main-panel shop-dashboard">
      <div className="page-heading">
        <div>
          <span className="mini-label">Butiksdesign</span>
          <h1>Rediger butik</h1>
          <p>Tilpas det navn, den farve og det logo, kunderne ser.</p>
        </div>
        <button className="secondary-action page-action" onClick={onBack} type="button">Tilbage til butik</button>
      </div>

      {error && <p className="status-line error">{error}</p>}

      <form className="shop-settings" onSubmit={(event) => {
        event.preventDefault();
        onSaveBranding({ shop_name: shopName, shop_color: shopColor, shop_categories: categories, description }, logo, cover);
      }}>
        <div className="shop-brand-fields">
          <label>Butiksnavn<input value={shopName} maxLength="120" onChange={(event) => setShopName(event.target.value)} required /></label>
          <label>Butiksfarve<span className="color-field"><input type="color" value={shopColor} onChange={(event) => setShopColor(event.target.value)} /><code>{shopColor}</code></span></label>
        </div>
        <label>Shopbeskrivelse<textarea maxLength="300" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Fortæl kort om din shop og hvad du sælger..." /></label>
        <label className="shop-logo-field">
          Vælg logo
          <span className="file-upload-control logo-picker">
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setLogo(event.target.files?.[0] || null)} />
            {logoPreviewUrl || shop?.shop_logo_url ? (
              <img className="selected-logo-preview" src={logoPreviewUrl || mediaUrl(shop.shop_logo_url)} alt="Valgt butikslogo" />
            ) : (
              <span className="logo-picker-placeholder"><strong>+</strong><small>Tilføj logo</small></span>
            )}
          </span>
        </label>
        <label className="shop-logo-field">
          Vælg coverbillede
          <span className="file-upload-control shop-cover-picker">
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setCover(event.target.files?.[0] || null)} />
            {coverPreviewUrl || shop?.cover_url ? <img className="selected-cover-preview" src={coverPreviewUrl || mediaUrl(shop.cover_url)} alt="Valgt cover" /> : <span className="logo-picker-placeholder"><strong>+</strong><small>Tilføj cover</small></span>}
          </span>
        </label>
        <div className="shop-settings-actions">
          <button className="primary-action" disabled={isSaving} type="submit">{isSaving ? "Gemmer..." : "Gem butiksdesign"}</button>
        </div>
      </form>

      <section className="shop-category-settings">
        <div><span className="mini-label">Interne kategorier</span><h2>Butikkens kategorier</h2><p>Lav kategorier, som kunder kan bruge til at filtrere varer i din butik.</p></div>
        <div className="shop-category-add">
          <input maxLength="80" value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder="For eksempel Kontor" />
          <button className="secondary-action" onClick={() => {
            const value = newCategory.trim();
            if (!value || categories.some((category) => category.toLocaleLowerCase("da-DK") === value.toLocaleLowerCase("da-DK"))) return;
            setCategories((current) => [...current, value]);
            setNewCategory("");
          }} type="button">Tilføj kategori</button>
        </div>
        {categories.length ? <div className="shop-category-tags">{categories.map((category) => <span key={category}>{category}<button aria-label={`Fjern ${category}`} onClick={() => setCategories((current) => current.filter((value) => value !== category))} type="button">×</button></span>)}</div> : <p className="status-line">Ingen interne kategorier endnu.</p>}
        <div className="shop-category-footer"><p className="shop-category-save-hint">Hvis du fjerner en kategori, bliver varer i den kategori sat til “Ingen”.</p><button className="primary-action" disabled={isSaving} onClick={() => onSaveBranding({ shop_name: shopName, shop_color: shopColor, shop_categories: categories, description }, null, null)} type="button">{isSaving ? "Gemmer..." : "Gem kategorier"}</button></div>
      </section>

      <div className="shop-preview-toolbar">
        <div><span className="mini-label">Live preview</span><h2>Sådan ser butikken ud</h2></div>
        <button className="secondary-action" onClick={onPreviewShop} type="button">Åbn fuld forhåndsvisning</button>
      </div>
      <ShopStorefrontPreview listings={listings} mediaUrl={mediaUrl} shop={previewShop} />

      <section className="shop-danger-zone">
        <div><span className="mini-label">Shopadministration</span><h2>Slet mini shop</h2><p>Shoppen og dens aktive varer arkiveres og forsvinder fra offentlig visning.</p></div>
        <button className="danger-secondary" disabled={isSaving} onClick={onDeleteShop} type="button">Slet mini shop</button>
      </section>
    </section>
  );
}

export default ShopSettingsPage;
